import React, { useCallback, useMemo, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';

const PROMO_PURPLE = '#9c27b0';
const PROMO_PURPLE_DEEP = '#6a1b9a';
const QIX_GREEN = '#52ac5b';
const QIX_GREEN_DEEP = '#306817';

/** Amount fragment: `10¢` or `$1.00` */
export function formatPayoutAmount(amount: number) {
  if (!Number.isFinite(amount) || amount <= 0) return '';
  return amount < 1 ? `${Math.round(amount * 100)}¢` : `$${amount.toFixed(2)}`;
}

/** Badge label matching WordPress image overlay. */
export function payoutBadgeText(amount: number, paymentType: number) {
  const price = formatPayoutAmount(amount);
  if (!price) return '+ click to earn';
  const text = `+ ${price}`;
  return paymentType === 1 ? `${text} vendor credits` : text;
}

/** Centered hover tip lines (no ad title/category). */
export function payoutTipLines(amount: number, paymentType: number): [string, string] {
  const price = formatPayoutAmount(amount);
  const amountLine = price ? `+ ${price}` : '+ click to earn';
  if (paymentType === 1) {
    return ['Click to earn vendor credits', amountLine];
  }
  return ['Click to earn credits', amountLine];
}

/** Resolve ProductsPortal origin for branding logo from an ad media URL. */
export function productsApiOriginFromImageUrl(imageUrl?: string | null): string {
  if (imageUrl) {
    try {
      return new URL(imageUrl).origin;
    } catch {
      /* fall through */
    }
  }
  const env = (typeof import.meta !== 'undefined'
    && (import.meta as any).env
    && (import.meta as any).env.VITE_PRODUCTS_API_BASE_URL) as string | undefined;
  if (env && String(env).trim()) {
    try {
      return new URL(String(env).trim()).origin;
    } catch {
      return String(env).trim().replace(/\/$/, '');
    }
  }
  return 'http://127.0.0.1:7100';
}

export function brandingLogoUrl(imageUrl?: string | null): string {
  return `${productsApiOriginFromImageUrl(imageUrl)}/api/branding/logo.png`;
}

export interface AdEarnImageProps {
  src: string;
  alt: string;
  /** Intrinsic / layout width hint; image scales to container. */
  width?: number;
  amount: number;
  paymentType: number;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  logoSrc?: string;
  logoFallbackSrc?: string;
}

/**
 * Creative image with lower-left brand+amount badge and centered hover tip
 * (WordPress / qixit-ads.js contract). Vendor credits use purple; Qix uses green.
 */
export default function AdEarnImage({
  src,
  alt,
  width,
  amount,
  paymentType,
  onClick,
  disabled,
  loading,
  logoSrc,
  logoFallbackSrc,
}: AdEarnImageProps) {
  const label = payoutBadgeText(amount, paymentType);
  const [tipLine1, tipLine2] = payoutTipLines(amount, paymentType);
  const isVendor = paymentType === 1;
  const resolvedLogo = useMemo(
    () => logoSrc || brandingLogoUrl(src),
    [logoSrc, src]
  );
  const [hovered, setHovered] = useState(false);

  const onEnter = useCallback(() => setHovered(true), []);
  const onLeave = useCallback(() => setHovered(false), []);

  const badgeBorder = isVendor ? PROMO_PURPLE : QIX_GREEN;
  const badgeColor = isVendor ? PROMO_PURPLE_DEEP : QIX_GREEN_DEEP;
  const badgeBg = isVendor
    ? 'linear-gradient(135deg, rgba(255,255,255,.95), rgba(248,235,252,.95))'
    : 'linear-gradient(135deg, rgba(255,255,255,.95), rgba(234,247,233,.95))';
  const tipBg = isVendor
    ? `linear-gradient(135deg, #ab47bc, ${PROMO_PURPLE_DEEP})`
    : `linear-gradient(135deg, ${QIX_GREEN}, ${QIX_GREEN_DEEP})`;

  return (
    <Box
      role={onClick ? 'button' : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      aria-label={`${tipLine1} ${tipLine2}`}
      onClick={disabled || loading ? undefined : onClick}
      onKeyDown={(e) => {
        if (disabled || loading || !onClick) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      sx={{
        position: 'relative',
        width: width || '100%',
        maxWidth: '100%',
        lineHeight: 0,
        cursor: disabled || loading ? 'default' : onClick ? 'pointer' : 'default',
        opacity: disabled ? 0.6 : 1,
        overflow: 'hidden',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: '#f7f7f7',
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{ width: '100%', height: 'auto', display: 'block', verticalAlign: 'top' }}
      />

      {/* Lower-left badge — always visible */}
      <Box
        sx={{
          position: 'absolute',
          left: 8,
          bottom: 8,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          pl: '4px',
          pr: 1.25,
          py: '4px',
          borderRadius: '4px',
          borderLeft: `3px solid ${badgeBorder}`,
          background: badgeBg,
          color: badgeColor,
          fontSize: 12,
          fontWeight: 700,
          lineHeight: 1.2,
          pointerEvents: 'none',
          zIndex: 2,
          boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
        }}
      >
        <Box
          component="img"
          src={resolvedLogo}
          alt=""
          onError={(e) => {
            const el = e.target as HTMLImageElement;
            const fallback = logoFallbackSrc || '/overlay-logo.svg';
            if (el.src !== fallback) el.src = fallback;
          }}
          sx={{
            // Sized under full badge height; py adds top/bottom border.
            // Arrow-"2" / Qix PNGs often have light backgrounds — multiply blends into the chip.
            height: 17,
            width: 'auto',
            maxWidth: 54,
            objectFit: 'contain',
            objectPosition: 'left center',
            display: 'block',
            flexShrink: 0,
            backgroundColor: 'transparent',
            mixBlendMode: 'multiply',
          }}
        />
        <Box component="span" sx={{ whiteSpace: 'nowrap', pr: '2px' }}>
          {label}
        </Box>
      </Box>

      {/* Centered hover tip — earn copy only */}
      <Box
        sx={{
          visibility: hovered ? 'visible' : 'hidden',
          opacity: hovered ? 1 : 0,
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 3,
          px: 1.25,
          py: 1,
          borderRadius: 1,
          color: '#fff',
          fontSize: 12,
          fontWeight: 700,
          textAlign: 'center',
          lineHeight: 1.35,
          background: tipBg,
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          boxShadow: '0 4px 12px rgba(0,0,0,0.28)',
          transition: 'opacity 0.15s ease, visibility 0.15s ease',
        }}
      >
        <span>{tipLine1}</span>
        <span>{tipLine2}</span>
      </Box>

      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.55)',
            zIndex: 4,
          }}
        >
          <CircularProgress size={28} />
        </Box>
      )}
    </Box>
  );
}
